import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Container, 
    Grid, 
    Typography, 
    Button, 
    Dialog,
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    TextField, 
    Switch, 
    FormControl, 
    Select, 
    MenuItem, 
    Paper, 
    Box, 
    Avatar, 
    IconButton, 
    Tooltip,
    InputAdornment} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Delete as DeleteIcon, Key as KeyIcon } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import WidgetAlert from 'src/widgets/WidgetAlert';
import AddUser from 'src/components/AddUser';
import services from 'src/services/Services';
import WidgetCustomLoader from 'src/widgets/WidgetCustomLoader';

const AdminUsers = () => {
    const [dataUsers, setDataUsers] = useState([]);
    const [dataRoles, setDataRoles] = useState([]);
    const [loading, setLoading] = useState(true);

    const [openModal, setOpenModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState({ name: '', idUser: '', idCliente: '' });

    const [alert, setAlert] = useState({ open: false, type: 'success', message: '' });
    const [searchText, setSearchText] = useState('');
        
        const filteredRows=useMemo(() => {
            return dataUsers.filter((row) =>
            Object.values(row).some((value) =>
         String(value).toLowerCase().includes(searchText.toLowerCase())
                )
              );
         }, [dataUsers, searchText]);

    const getUsers = useCallback(async () => {
        const res = await services.getUsersWithRolAssignedMysql();
        proccesDataUsers(res.data);
    }, []);

    useEffect(() => {
        const fetchRoles = async () => {
            const res = await services.getRolesClient();
            const roles = res.data;

            const uniqueRolesMap = new Map();
            roles.forEach((r) => {
                if (!uniqueRolesMap.has(r.ID)) {
                    uniqueRolesMap.set(r.ID, r);
                }
            });

            const uniqueRoles = Array.from(uniqueRolesMap.values());

            setDataRoles(uniqueRoles);
        };
        fetchRoles();
    }, []);

    useEffect(() => {
        if (dataRoles.length > 0) {
            const fetchUsers = async () => {
                const res = await services.getUsersWithRolAssignedMysql();
                proccesDataUsers(res.data);
            };
            fetchUsers();
        }
    }, [dataRoles]);

    const proccesDataUsers = (data) => {
        const grouped = data.reduce((acc, line) => {
            acc[line.FIRST_NAME] = acc[line.FIRST_NAME] || [];
            acc[line.FIRST_NAME].push(line);
            return acc;
        }, {});

        const dataFinTemp = Object.keys(grouped).map((key) => {
            const first = grouped[key][0];
            const item = {
                USERNAME: first.USERNAME,
                ID_USUARIO: first.ID_USUARIO,
                ENABLED: first.ENABLED,
                ROL: first.ROL,
                EMAIL: first.EMAIL,
                FIRST_NAME: first.FIRST_NAME,
                FECHA_CREACION: first.FECHA_CREACION,
                TIPOUSUARIO: '',
                IDCLIENTE: '',
                IDMYSQL: '',
            };

            grouped[key].forEach((obj) => {
                if (obj.ATRIBUTO === 'IDCLIENTE') item.IDCLIENTE = obj.VALOR;
                if (obj.ATRIBUTO === 'TIPOUSUARIO') item.TIPOUSUARIO = obj.VALOR;
                if (obj.ATRIBUTO === 'IDMYSQL') item.IDMYSQL = obj.VALOR;
            });

            return item;
        });

        setDataUsers(dataFinTemp);
        setLoading(false);
    };

    const handleDeleteUser = async () => {
        try {
            await services.deleteUser(selectedUser.idUser);
            await services.deleteUserMysql({ IdCliente: String(selectedUser.idCliente) });
            setAlert({ open: true, type: 'success', message: '¡Se ha eliminado el usuario!' });
            setOpenModal(false);
            getUsers();
        } catch (error) {
            console.error(error);
            setAlert({ open: true, type: 'error', message: 'Error al eliminar usuario' });
        }
    };

    const handleResetPass = async (row) => {
        try {
            const body = { type: 'password', value: 'icintracom', temporary: true };
            await services.resetPassword(row.ID_USUARIO, body);
            setAlert({ open: true, type: 'success', message: 'Se ha reiniciado la contraseña' });
        } catch (error) {
            console.error(error);
            setAlert({ open: true, type: 'error', message: 'Error al resetear contraseña' });
        }
    };

    const columns = [
        { field: 'IDCLIENTE', headerName: 'ID Cliente', flex: 1 },
        { field: 'USERNAME', headerName: 'Usuario', flex: 1 },
        { field: 'FIRST_NAME', headerName: 'Nombre', flex: 1 },
        {
            field: 'EMAIL',
            headerName: 'Email',
            flex: 1.5,
            renderCell: (params) => (
                <TextField
                    variant="outlined"
                    size="small"
                    defaultValue={params.row.EMAIL}
                    onBlur={(e) => {
                        const body = { email: e.target.value };
                        services.updateUser(params.row.ID_USUARIO, body);
                        setAlert({ open: true, type: 'success', message: '¡Se ha actualizado el email!' });
                    }}
                    sx={{ width: '100%' }}
                />
            ),
        },
        { field: 'FECHA_CREACION', headerName: 'Fecha Creación', flex: 1 },
        {
            field: 'ROL',
            headerName: 'Rol',
            flex: 1,
            renderCell: (params) => {
                const userRol = dataRoles.find(r => r.NAME === params.row.ROL);
                const uniqueRoles = Array.from(new Map(dataRoles.map(r => [r.ID, r])).values());

                return (
                    <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                        <Select
                            value={userRol?.ID || ''}
                            onChange={async (e) => {
                                const rolID = e.target.value;
                                const rolName = uniqueRoles.find(r => r.ID === rolID)?.NAME;

                                const body = [{ id: rolID, name: rolName, containerId: '2758d422-8dff-4b47-9706-40559e829d47' }];
                                try {
                                    const assigned = (await services.getAllRolesAssigned(params.row.ID_USUARIO)).data;
                                    await services.deleteRolAssigned(params.row.ID_USUARIO, assigned);
                                    await services.setRoleUser(params.row.ID_USUARIO, body);

                                    params.row.ROL = rolName;

                                    setAlert({ open: true, type: 'success', message: '¡Se ha actualizado el rol!' });
                                } catch (err) {
                                    console.error(err);
                                    setAlert({ open: true, type: 'error', message: 'Error al actualizar el rol' });
                                }
                            }}
                        >
                            {uniqueRoles.map(r => (
                                <MenuItem key={r.ID} value={r.ID} title={r.NAME}>
                                    {r.NAME}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            },
        },
        {
            field: 'ENABLED',
            headerName: 'Activado',
            flex: 0.5,
            renderCell: ({ row }) => (
                <Switch
                    checked={Boolean(row.ENABLED)}
                    onChange={async (e) => {
                        const enabled = e.target.checked;

                        await services.updateActivateUser(
                            row.ID_USUARIO,
                            { enabled }
                        ).then(()=>{
                            setDataUsers((prev) =>
                                prev.map((u) =>
                                    u.ID_USUARIO === row.ID_USUARIO
                                        ? { ...u, ENABLED: enabled }
                                        : u
                                )
                            );
                            setAlert({
                                open: true,
                                type: 'success',
                                message: '¡Se ha actualizado la activación!',
                            });
                        })
                    }}
                />
            ),
        },
        {
            field: 'RESET',
            headerName: 'Reset Pass.',
            flex: 0.5,
            renderCell: (params) => (
                <Tooltip title="Reiniciar contraseña">
                    <IconButton size="small" onClick={() => handleResetPass(params.row)}>
                        <KeyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
        {
            field: 'DELETE',
            headerName: 'Eliminar',
            flex: 0.5,
            renderCell: (params) =>
                params.row.USERNAME === 'admin' ? null : (
                    <Tooltip title="Eliminar usuario">
                        <IconButton
                            size="small"
                            onClick={() =>
                                setSelectedUser({
                                    name: params.row.USERNAME,
                                    idUser: params.row.ID_USUARIO,
                                    idCliente: params.row.IDMYSQL,
                                }) & setOpenModal(true)
                            }
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ),
        },
        { field: 'TIPOUSUARIO', headerName: 'Tipo Usuario', flex: 1 },
    ];

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <WidgetAlert open={alert.open} type={alert.type} message={alert.message} onClose={() => setAlert({ ...alert, open: false })} />

            <Paper elevation={0} sx={{ mb: 3, p: 2, borderRadius: 3, background: 'linear-gradient(180deg, #fff, #fbfcff)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'transparent', width: 56, height: 56, boxShadow: '0 8px 24px rgba(16,24,40,0.06)' }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 12C14.2091 12 16 10.2091 16 8C16 5.79086 14.2091 4 12 4C9.79086 4 8 5.79086 8 8C8 10.2091 9.79086 12 12 12Z" stroke="#1976d2" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M20 20C20 16.6863 16.4183 14 12 14C7.58172 14 4 16.6863 4 20" stroke="#1976d2" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </Avatar>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Administración de Usuarios</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Gestiona cuentas, roles y acceso</Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Paper elevation={1} sx={{ px: 2, py: 1, borderRadius: 2, minWidth: 160 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Total Usuarios</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>{dataUsers.length}</Typography>
                        </Paper>

                        <Paper elevation={1} sx={{ px: 2, py: 1, borderRadius: 2, minWidth: 160 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Total Clientes</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>{dataUsers.filter((u) => u.TIPOUSUARIO.toUpperCase() === 'CLIENTE').length}</Typography>
                        </Paper>

                        <Paper elevation={1} sx={{ px: 2, py: 1, borderRadius: 2, minWidth: 160 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Total Internos</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>{dataUsers.filter((u) => u.TIPOUSUARIO.toUpperCase() === 'ICINTRACOM').length}</Typography>
                        </Paper>

                        <Paper elevation={1} sx={{ px: 2, py: 1, borderRadius: 2, minWidth: 160 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Total Administradores</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>{dataUsers.filter((u) => u.TIPOUSUARIO === 'admin').length}</Typography>
                        </Paper>

                        <Box sx={{ ml: 1 }}>
                            <AddUser roles={dataRoles} dataUsers={dataUsers} getUsers={getUsers} />
                        </Box>
                    </Box>
                </Box>
            </Paper>

            {loading ? (
                <WidgetCustomLoader />
            ) : (
                <Paper elevation={1} sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 12px 40px rgba(16,24,40,0.06)' }}>
                    <div style={{ height: 600, width: '100%' }}>
                        <TextField
                        
                                            size="small"
                        
                                            placeholder="Buscar…"
                        
                                            value={searchText}
                        
                                            onChange={(e) => setSearchText(e.target.value)}
                        
                                            InputProps={{
                        
                                                startAdornment: (
                        <InputAdornment position="start">
                        <SearchIcon />
                        </InputAdornment>
                        
                                                ),
                        
                                            }}
                        
                                        />
                        <DataGrid
                            rows={filteredRows}
                            columns={columns}
                            pageSize={10}
                            rowsPerPageOptions={[5, 10, 20]}
                            disableSelectionOnClick
                            getRowId={(row) => row.ID_USUARIO}
                            sx={{
                                '& .MuiDataGrid-root': { border: 'none' },
                                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f3f7fb', fontWeight: 800, borderBottom: '1px solid rgba(0,0,0,0.04)' },
                                '& .MuiDataGrid-cell': { alignItems: 'center' },
                                '& .MuiDataGrid-row:hover': { backgroundColor: '#fbfdff' },
                                '& .MuiDataGrid-footerContainer': { backgroundColor: '#fafcff', borderTop: '1px solid rgba(0,0,0,0.04)' },
                                '& .MuiDataGrid-virtualScroller': { background: 'linear-gradient(180deg, #fff, #fbfdff)' },
                                '& .MuiDataGrid-toolbarContainer': { px: 2 },
                                '& .MuiDataGrid-row': { transition: 'transform .12s ease' },
                            }}
                        />
                    </div>
                </Paper>
            )}

            <Dialog open={openModal} onClose={() => setOpenModal(false)}>
                <DialogTitle>Eliminar Usuario</DialogTitle>
                <DialogContent>¿Eliminar el usuario <b>{selectedUser.name}</b>?</DialogContent>
                <DialogActions>
                    <Button variant="contained" color="primary" onClick={handleDeleteUser} sx={{ textTransform: 'none', fontWeight: 700 }}>
                        Aceptar
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={() => setOpenModal(false)} sx={{ textTransform: 'none' }}>
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AdminUsers;
